import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Layout, Menu, Badge } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import type { MenuProps } from "antd";

import logo from "../../assets/sdtc.svg";
import logoIcon from "../../assets/sdtc-icon.svg";
import iconUser from "../../assets/icon-sidebar/user.svg";
import iconStaff from "../../assets/icon-sidebar/users-round.svg";
import iconFileText from "../../assets/icon-sidebar/file-text.svg";
import iconHouse from "../../assets/icon-sidebar/house-heart.svg";
import iconCalendarRange from "../../assets/icon-sidebar/calendar-range.svg";
import iconSalary from "../../assets/icon-sidebar/badge-dollar-sign.svg";
import iconSettings from "../../assets/icon-sidebar/settings.svg";

import styles from "./Sidebar.module.css";
import { useAuth } from "../../contexts/AuthContext";
import { usersService } from "../../services/users.service";
import { useNotificationSocketHeader } from "../../hooks/useNotificationSocketHeader";
import { can, canAny } from "@shared/utils";
import { UserCog } from "lucide-react";

const { Sider } = Layout;

interface SidebarProps {
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  onDragWidthChange?: (width: number | null) => void;
}

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 80;
const DRAG_SNAP_THRESHOLD = (SIDEBAR_WIDTH + SIDEBAR_COLLAPSED_WIDTH) / 2; // midpoint

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed: collapsedProp,
  onCollapse,
  onDragWidthChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [collapsedInternal, setCollapsedInternal] = useState(true);
  const [openKeys, setOpenKeys] = useState<string[]>([
    "personal",
    "management",
  ]);
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const [pendingUpdateRequestsCount, setPendingUpdateRequestsCount] =
    useState<number>(0);
  const [dragWidth, setDragWidth] = useState<number | null>(null);

  const collapsed = collapsedProp ?? collapsedInternal;
  const isDragging = dragWidth !== null;

  const setCollapsed = useCallback(
    (next: boolean) => {
      if (collapsedProp === undefined) {
        setCollapsedInternal(next);
      }
      onCollapse?.(next);
    },
    [collapsedProp, onCollapse],
  );

  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, v));

  const handleDragStart: React.PointerEventHandler<HTMLDivElement> = (e) => {
    // Only left mouse / primary pointer
    if (e.button !== 0) return;

    const startWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
    const startX = e.clientX;

    // We'll drive width manually while dragging; avoid hover auto-collapse during drag
    setIsHoverExpanded(false);

    (e.currentTarget as HTMLDivElement).setPointerCapture?.(e.pointerId);

    setDragWidth(startWidth);
    onDragWidthChange?.(startWidth);

    const onMove = (ev: PointerEvent) => {
      const nextWidth = clamp(
        startWidth + (ev.clientX - startX),
        SIDEBAR_COLLAPSED_WIDTH,
        SIDEBAR_WIDTH,
      );
      setDragWidth(nextWidth);
      onDragWidthChange?.(nextWidth);
    };

    const onEnd = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);

      const endWidth = clamp(
        startWidth + (ev.clientX - startX),
        SIDEBAR_COLLAPSED_WIDTH,
        SIDEBAR_WIDTH,
      );
      const shouldCollapse = endWidth < DRAG_SNAP_THRESHOLD;

      setDragWidth(null);
      onDragWidthChange?.(null);
      setCollapsed(shouldCollapse);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
  };

  const handleMouseEnter = () => {
    if (isDragging) return;
    if (collapsed) {
      setCollapsed(false);
      setIsHoverExpanded(true);
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) return;
    if (!isHoverExpanded) return;

    // Close immediately (but still animates via CSS transition) to avoid "laggy" feel.
    setCollapsed(true);
    setIsHoverExpanded(false);
  };

  // Lấy số lượng yêu cầu cập nhật đang chờ xử lý
  const fetchPendingCount = useCallback(async () => {
    // Chỉ fetch nếu user không phải là staff
    if (can(user, "user", "list")) {
      try {
        const response = await usersService.getUpdateRequests({
          status: "pending",
          limit: 1000,
        });
        setPendingUpdateRequestsCount(response.data.length);
      } catch (error) {
        // Bỏ qua lỗi
        console.error("Lỗi khi lấy số lượng yêu cầu cập nhật:", error);
      }
    } else {
      // Đặt lại số lượng nếu user là staff hoặc chưa đăng nhập
      setPendingUpdateRequestsCount(0);
    }
  }, [user]);

  // Cập nhật realtime qua notification socket - làm mới ngay lập tức
  useNotificationSocketHeader({
    onNewNotification: () => {
      // Làm mới ngay khi có thông báo mới (không delay)
      // Sẽ bắt được thông báo về yêu cầu cập nhật mới/đã duyệt/đã từ chối
      fetchPendingCount();
    },
    onNotificationRead: () => {
      // Làm mới khi đọc thông báo trong trường hợp thông báo về yêu cầu cập nhật
      fetchPendingCount();
    },
    onRefresh: () => {
      // Làm mới ngay khi có sự kiện refresh
      fetchPendingCount();
    },
  });

  // Lấy dữ liệu ban đầu và polling liên tục để cập nhật realtime
  useEffect(() => {
    // Lấy ngay khi mount và khi user thay đổi
    if (can(user, "user", "list")) {
      fetchPendingCount();
    }

    // Polling liên tục: Làm mới mỗi 10 giây bất kể trạng thái socket
    // Đảm bảo số lượng luôn được cập nhật ngay cả khi bỏ lỡ sự kiện socket
    const interval = setInterval(() => {
      if (can(user, "user", "list")) {
        fetchPendingCount();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [user, fetchPendingCount]);

  // Làm mới số lượng khi điều hướng đến trang nhân sự
  useEffect(() => {
    if (
      location.pathname.includes("/management/personnel") &&
      can(user, "user", "list")
    ) {
      fetchPendingCount();
    }
  }, [location.pathname, fetchPendingCount, user]);

  const selectedKeys = useMemo(() => {
    const path = location.pathname;
    if (path === "/") return ["overview-management"];

    if (path.includes("/management/personnel")) return ["personnel-management"];
    if (path.includes("/management/role-permissions"))
      return ["role-permissions-management"];
    if (path.includes("/management/timekeeping"))
      return ["timekeeping-management"];
    if (path.includes("/management/salary/list")) return ["salary-list"];
    if (path.includes("/management/salary/timesheet"))
      return ["salary-timesheet"];
    if (path.includes("/management/salary")) return ["salary"];
    if (path.includes("/management/department")) return ["departments"];
    if (path.includes("/management/reports")) return ["reports-management"];
    if (path.includes("/management/activity-logs")) return ["activity-logs"];
    if (path.includes("/management/settings")) return ["settings"];

    if (path.includes("/personal/timekeeping")) return ["timekeeping-personal"];
    if (path.includes("/personal/applications")) return ["applications"];
    if (path.includes("/personal/payroll")) return ["payroll"];
    if (path.includes("/personal/reports")) return ["reports-personal"];
    if (path.includes("/personal/profile")) return ["profile"];
    if (path.includes("/personal")) return ["overview-personal"];

    return ["overview-management"];
  }, [location.pathname]);

  const menuItems = useMemo<MenuProps["items"]>(() => {
    const canActivityLogRead = can(user, "activity_log", "read");
    const canPersonnelList = can(user, "user", "list");
    const canAttendanceRead = can(user, "attendance", "read");
    const canPayrollRead = can(user, "payroll", "read");
    const canDepartmentRead = can(user, "department", "read");
    const items: MenuProps["items"] = [
      {
        key: "personal",
        label: (
          <span className={styles.sectionLabel} style={{ fontWeight: 700 }}>
            CÁ NHÂN
          </span>
        ),
        type: "submenu",
        children: [
          {
            key: "profile",
            icon: <img src={iconUser} alt="" className={styles.menuIcon} />,
            label: "Hồ sơ cá nhân",
            onClick: () => navigate("/personal/profile"),
          },
        ],
      },
    ];

    if (
      canAny(user, [
        ["user", "list"],
        ["attendance", "read"],
        ["payroll", "read"],
        ["department", "read"],
        ["activity_log", "read"],
      ])
    ) {
      items.push({
        key: "management",
        label: (
          <span className={styles.sectionLabel} style={{ fontWeight: 700 }}>
            QUẢN LÝ
          </span>
        ),
        type: "submenu",
        children: [
          ...(canPersonnelList ? [{
            key: "personnel-management",
            icon: (
              <Badge
                count={
                  pendingUpdateRequestsCount > 0
                    ? pendingUpdateRequestsCount
                    : 0
                }
                size="small"
                offset={[-2, 4]}
                showZero={false}
              >
                <img src={iconStaff} alt="" className={styles.menuIcon} />
              </Badge>
            ),
            label: "Nhân sự",
            onClick: () => navigate("/management/personnel"),
          }] : []),
          ...(user?.role === "admin"
            ? [
                {
                  key: "role-permissions-management",
                  icon: <UserCog className={styles.menuIcon} strokeWidth={1.5} />,
                  label: "Quyền hạn & Vai trò",
                  onClick: () => navigate("/management/role-permissions"),
                },
              ]
            : []),
          ...(canDepartmentRead ? [{
            key: "departments",
            icon: <img src={iconHouse} alt="" className={styles.menuIcon} />,
            label: "Phòng ban",
            onClick: () => navigate("/management/department"),
          }] : []),
          ...(canAttendanceRead ? [{
            key: "timekeeping-management",
            icon: (
              <img src={iconCalendarRange} alt="" className={styles.menuIcon} />
            ),
            label: "Chấm công",
            onClick: () => navigate("/management/timekeeping"),
          }] : []),
          ...(canPayrollRead ? [{
            key: "salary",
            label: <span className={styles.hideWhenCollapsed}>Tính lương</span>,
            icon: <img src={iconSalary} alt="" className={styles.menuIcon} />,
            type: "submenu" as const,
            children: [
              {
                key: "salary-timesheet",
                icon: (
                  <img src={iconFileText} alt="" className={styles.menuIcon} />
                ),
                label: "Bảng chấm công",
                onClick: () => navigate("/management/salary/timesheet-table"),
              },
            ],
          }] : []),
          ...(canActivityLogRead
            ? [
                {
                  key: "activity-logs",
                  icon: (
                    <img
                      src={iconFileText}
                      alt=""
                      className={styles.menuIcon}
                    />
                  ),
                  label: "Nhật ký hoạt động",
                  onClick: () => navigate("/management/activity-logs"),
                },
              ]
            : []),
          ...(canPersonnelList ? [{
            key: "settings",
            icon: <img src={iconSettings} alt="" className={styles.menuIcon} />,
            label: "Cài đặt",
            onClick: () => navigate("/management/settings"),
          }] : []),
        ],
      });
    }

    return items;
  }, [user, navigate, pendingUpdateRequestsCount]);

  return (
    <Sider
      width={dragWidth ?? SIDEBAR_WIDTH}
      collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
      collapsed={collapsed && !isDragging}
      className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ""} ${isDragging ? styles.sidebarDragging : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={styles.dragHandle}
        onPointerDown={handleDragStart}
        aria-hidden="true"
      />
      <div className={styles.logoContainer}>
        <img
          src={collapsed ? logoIcon : logo}
          alt="SEA DRAGON TECHNOLOGY"
          style={{
            height: "60px",
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
          }}
        />
      </div>

      <Menu
        mode="inline"
        inlineCollapsed={false}
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        items={menuItems}
        className={styles.menu}
        theme="light"
      />
    </Sider>
  );
};
